import type {
  MerchantFeaturedItemDto,
  MerchantFeaturedItemRequestDto,
} from '@kardume/types';
import api from './http';

/**
 * Produtos e serviços em destaque no painel do lojista (card #218, §4 do spec
 * 033).
 *
 * **A loja sai do token, nunca da URL.** Nenhuma rota aqui tem `{merchantId}`:
 * o prefixo é `/v1/merchant/featured-items`, ao lado de `/v1/merchant/profile`,
 * e é o mesmo par de permissões - quem edita o perfil edita a vitrine.
 *
 * Cliente próprio, e não mais um bloco no `adminApi`, pelo mesmo motivo do
 * `merchantExperienceApi`: o endpoint é novo e o arquivo é novo, então nenhuma
 * outra tela corre risco por causa deste card.
 */
export const merchantFeaturedItemApi = {
  /** Lista viva da loja, já ordenada por `position`. */
  list(): Promise<MerchantFeaturedItemDto[]> {
    return api.get<MerchantFeaturedItemDto[]>('/v1/merchant/featured-items').then((r) => r.data);
  },

  /** Entra no **fim** da lista - a `position` é do servidor, não da tela. */
  create(payload: MerchantFeaturedItemRequestDto): Promise<MerchantFeaturedItemDto> {
    return api
      .post<MerchantFeaturedItemDto>('/v1/merchant/featured-items', payload)
      .then((r) => r.data);
  },

  update(
    itemId: string,
    payload: MerchantFeaturedItemRequestDto
  ): Promise<MerchantFeaturedItemDto> {
    return api
      .put<MerchantFeaturedItemDto>(`/v1/merchant/featured-items/${itemId}`, payload)
      .then((r) => r.data);
  },

  /** Soft delete. O core compacta as posições restantes; a tela não as recalcula. */
  remove(itemId: string): Promise<void> {
    return api.delete(`/v1/merchant/featured-items/${itemId}`).then(() => undefined);
  },

  /**
   * Reordena mandando **a lista inteira de ids**, na ordem nova.
   *
   * Não existe endpoint de mover-um-degrau de propósito (§4 do spec 033):
   * arrastar um item do fim para o começo seria N chamadas, e cada uma delas é
   * uma chance de a tela e o banco discordarem no meio. O corpo precisa conter
   * exatamente os ids vivos da loja - divergiu, o core recusa e nada é gravado.
   *
   * A resposta é a lista já reordenada: é ela que a tela adota, e não o palpite
   * otimista que ela desenhou enquanto o arrasto acontecia.
   */
  reorder(ids: string[]): Promise<MerchantFeaturedItemDto[]> {
    return api
      .put<MerchantFeaturedItemDto[]>('/v1/merchant/featured-items/order', { ids })
      .then((r) => r.data);
  },
};
